import { FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';
import { isEmpty, map } from 'es-toolkit/compat';
import React from 'react';

import { SpotDetectionCountBin } from 'interfaces/calculateFeatures';
import { HScoreBinsEditor } from './HScoreBinsEditor';

export interface ProbeCountBinsSetup {
  countBins?: SpotDetectionCountBin[];
  positivityThresholdScore?: number;
}

interface PerProbeCountBinsOverridesProps {
  probeNames: string[];
  probeDisplayNames: Record<string, string>;
  generalCountBins: SpotDetectionCountBin[];
  generalPositivityThresholdScore?: number;
  perProbeSetup?: Record<string, ProbeCountBinsSetup>;
  onProbeSetupChange: (probeName: string, setup: ProbeCountBinsSetup | undefined) => void;
  showPositivityThreshold?: boolean;
}

// A probe with no override here inherits the general H-score bins above; overriding only makes sense
// once there is more than one probe to distinguish between.
export const PerProbeCountBinsOverrides: React.FC<PerProbeCountBinsOverridesProps> = ({
  probeNames,
  probeDisplayNames,
  generalCountBins,
  generalPositivityThresholdScore,
  perProbeSetup,
  onProbeSetupChange,
  showPositivityThreshold = true,
}) => {
  if (probeNames.length < 2) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Per-probe overrides
      </Typography>
      <Typography variant="body2" color="text.secondary" display="block">
        Every probe uses the H-score bins above by default. Override a probe only when its expected spot counts differ
        from the rest.
      </Typography>
      <Stack spacing={1}>
        {map(probeNames, (probeName) => {
          const override = perProbeSetup?.[probeName];
          const hasOverride = !isEmpty(override?.countBins);

          return (
            <Paper
              key={probeName}
              variant="outlined"
              role="group"
              aria-label={`${probeDisplayNames[probeName] ?? probeName} H-score bins`}
              sx={{ p: 1.5 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={hasOverride}
                    onChange={(_event, checked) =>
                      onProbeSetupChange(
                        probeName,
                        checked
                          ? {
                              countBins: map(generalCountBins, (countBin) => ({ ...countBin })),
                              positivityThresholdScore: generalPositivityThresholdScore,
                            }
                          : undefined
                      )
                    }
                  />
                }
                label={`Override bins for ${probeDisplayNames[probeName] ?? probeName}`}
              />
              {hasOverride && (
                <HScoreBinsEditor
                  showPositivityThreshold={showPositivityThreshold}
                  countBins={override?.countBins || []}
                  onChange={(countBins) => onProbeSetupChange(probeName, { ...override, countBins })}
                  positivityThresholdScore={override?.positivityThresholdScore}
                  onPositivityThresholdScoreChange={(positivityThresholdScore) =>
                    onProbeSetupChange(probeName, { ...override, positivityThresholdScore })
                  }
                />
              )}
            </Paper>
          );
        })}
      </Stack>
    </Stack>
  );
};
